import { useCallback, useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useProjects } from './useProjects';
import { 
  findProject, 
  getProjectId, 
  getProjectName,
  getProjectNumber,
  isValidProject
} from '../utils/projectUtils';

/**
 * Centralized project management hook that ensures all API calls use the same project ID and name
 * This hook provides a single source of truth for project data across all components
 */
export const useProjectManager = () => {
  const { projects, projectsLoading, projectsError } = useProjects();
  const { currentProject } = useSelector(state => state.weldData);
  
  // State for the resolved project data from live API
  const [resolvedProject, setResolvedProject] = useState(null);
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  // Resolve project from live data using exact project number and name match
  const resolveProject = useCallback((projectNumber, projectName) => {
    if (!projects || !Array.isArray(projects) || !projectNumber || !projectName) {
      return null;
    }

    // Find project by exact match of both project number and name
    const foundProject = projects.find(project => {
      const numberMatch = project.project_number === projectNumber;
      const nameMatch = project.project_name === projectName;
      return numberMatch && nameMatch;
    });

    return foundProject || null;
  }, [projects]);

  // Auto-resolve project when currentProject changes
  useEffect(() => {
    if (currentProject?.project?.projectNumber && currentProject?.project?.projectName) {
      setIsResolving(true);
      setResolveError(null);
      
      const projectNumber = currentProject.project.projectNumber;
      const projectName = currentProject.project.projectName;
      const projectId = currentProject.project.projectId;
      
      // Only try to resolve from live projects if we have them
      if (projects?.length > 0) {
        const resolved = resolveProject(projectNumber, projectName);
        
        if (resolved) {
          setResolvedProject(resolved);
        } else {
          // FALLBACK: Create a minimal project object from currentProject data
          const fallbackProject = {
            project_id: projectId,
            project_name: projectName,
            project_number: projectNumber
          };
          
          setResolvedProject(fallbackProject);
          setResolveError(null); // Clear error since we have a fallback
        }
      } else {
        // No live projects yet, use fallback immediately
        const fallbackProject = {
          project_id: projectId,
          project_name: projectName,
          project_number: projectNumber
        };
        setResolvedProject(fallbackProject);
        setResolveError(null);
      }
      
      setIsResolving(false);
    } else {
      setResolvedProject(null);
      setResolveError(null);
    }
  }, [currentProject, projects, resolveProject]);

  // Get the project ID and name for API calls
  const getProjectDataForApi = useCallback(() => {
    if (!resolvedProject) {
      return { projectId: null, projectName: null, isResolved: false };
    }

    return {
      projectId: getProjectId(resolvedProject),
      projectName: getProjectName(resolvedProject),
      projectNumber: getProjectNumber(resolvedProject),
      isResolved: true,
      project: resolvedProject
    };
  }, [resolvedProject]);

  // Memoized project data to prevent unnecessary re-renders
  const projectData = useMemo(() => {
    return getProjectDataForApi();
  }, [getProjectDataForApi]);

  // Check if project is ready for API calls
  const isProjectReady = useMemo(() => {
    return projectData.isResolved && projectData.projectId && projectData.projectName;
  }, [projectData]);

  // Get project by identifier (for backward compatibility)
  const findProjectByIdentifier = useCallback((identifier) => {
    if (!projects || !Array.isArray(projects) || !identifier) {
      return null;
    }
    return findProject(projects, identifier);
  }, [projects]);

  // Get project by name and number (exact match)
  const findProjectByNameAndNumber = useCallback((projectName, projectNumber) => {
    if (!projects || !Array.isArray(projects) || !projectName || !projectNumber) {
      return null;
    }

    return projects.find(project => {
      const nameMatch = project.project_name === projectName;
      const numberMatch = project.project_number === projectNumber;
      return nameMatch && numberMatch;
    });
  }, [projects]);

  return {
    // Project data for API calls
    projectId: projectData.projectId,
    projectName: projectData.projectName,
    projectNumber: projectData.projectNumber,
    project: projectData.project,
    isProjectReady,
    
    // Resolution state
    isResolving,
    resolveError,
    isResolved: projectData.isResolved,
    
    // Live projects data
    projects,
    projectsLoading,
    projectsError,
    
    // Current project from Redux
    currentProject,
    
    // Utility functions
    findProjectByIdentifier,
    findProjectByNameAndNumber,
    resolveProject,
    getProjectDataForApi
  };
};
